import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-installer-log-view-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './installer-log-view-dialog.component.html',
  styleUrl: './installer-log-view-dialog.component.scss',
})
export class InstallerLogViewDialogComponent implements OnInit {

  headers: string[] = [];
  allRows: string[][] = [];
  error = '';

  // Search
  searchText = '';

  // Sort
  sortColIndex = -1;
  sortDir: 'asc' | 'desc' = 'asc';

  // Pagination
  readonly pageSize = 50;
  currentPage = 0;

  // Right-click context menu
  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };
  selectedCellValue = '';

  // ── Computed ──────────────────────────────────────────────────────────────
  get filteredRows(): string[][] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.allRows;
    return this.allRows.filter(row => row.some(cell => cell.toLowerCase().includes(q)));
  }

  get sortedRows(): string[][] {
    const rows = [...this.filteredRows];
    if (this.sortColIndex < 0) return rows;
    const idx = this.sortColIndex;
    const dir = this.sortDir;
    return rows.sort((a, b) => {
      const cmp = (a[idx] ?? '').localeCompare(b[idx] ?? '', undefined, { numeric: true, sensitivity: 'base' });
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  get pagedRows(): string[][] {
    const start = this.currentPage * this.pageSize;
    return this.sortedRows.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get fromRow(): number {
    return this.filteredRows.length === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get toRow(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.filteredRows.length);
  }

  get showPagination(): boolean {
    return this.filteredRows.length > this.pageSize;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { content: string; fileName: string },
    public dialogRef: MatDialogRef<InstallerLogViewDialogComponent>
  ) {}

  ngOnInit(): void {
    try {
      const parsed = this.parseCsv(this.data.content ?? '');
      this.headers = parsed.headers;
      this.allRows = parsed.rows;
    } catch {
      this.error = 'Failed to parse CSV content.';
    }
  }

  /**
   * RFC-style CSV parse that keeps newlines inside quoted fields as cell content
   * (not new rows). Matches how Excel counts records — naive split(/\n/) over-counts.
   */
  private parseCsv(content: string): { headers: string[]; rows: string[][] } {
    const rows = this.parseCsvRecords(content);
    if (rows.length === 0) {
      return { headers: [], rows: [] };
    }
    return {
      headers: rows[0],
      rows: rows.slice(1),
    };
  }

  private parseCsvRecords(content: string): string[][] {
    const records: string[][] = [];
    let row: string[] = [];
    let current = '';
    let inQuotes = false;
    const text = String(content ?? '');

    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const next = text[i + 1];

      if (ch === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (ch === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
        continue;
      }

      if ((ch === '\n' || ch === '\r') && !inQuotes) {
        if (ch === '\r' && next === '\n') {
          i++;
        }
        row.push(current.trim());
        current = '';
        if (row.some((cell) => cell.length > 0)) {
          records.push(row);
        }
        row = [];
        continue;
      }

      current += ch;
    }

    row.push(current.trim());
    if (row.some((cell) => cell.length > 0)) {
      records.push(row);
    }
    return records;
  }

  /** @deprecated kept for any residual callers — prefer parseCsvRecords */
  parseLine(line: string): string[] {
    const records = this.parseCsvRecords(line);
    return records[0] ?? [];
  }

  // ── Right-click context menu ───────────────────────────────────────────────
  onCellContextMenu(event: MouseEvent, cellValue: string) {
    event.preventDefault();
    this.selectedCellValue = cellValue;
    this.contextMenuPosition = { x: event.clientX, y: event.clientY };
    this.contextMenuVisible = true;
  }

  handleCopy() {
    navigator.clipboard.writeText(this.selectedCellValue).then(() => {
      this.contextMenuVisible = false;
    });
  }

  preventDefaultContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.contextMenuVisible = false;
  }

  // ── Table interactions ─────────────────────────────────────────────────────
  onSearch() { this.currentPage = 0; }

  clearSearch() { this.searchText = ''; this.currentPage = 0; }

  sortBy(colIndex: number) {
    if (this.sortColIndex === colIndex) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColIndex = colIndex;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
  }

  prevPage() { if (this.currentPage > 0) this.currentPage--; }
  nextPage() { if (this.currentPage < this.totalPages - 1) this.currentPage++; }

  cellMatches(cell: string): boolean {
    const q = this.searchText.trim().toLowerCase();
    return !!q && cell.toLowerCase().includes(q);
  }

  close() { this.dialogRef.close(); }

  /**
   * Converts a raw CSV header (snake_case, camelCase, UPPER_CASE, or plain)
   * into Title Case words.
   * e.g. "device_id" → "Device Id"
   *      "createdAt"  → "Created At"
   *      "GATEWAY_ID" → "Gateway Id"
   */
  toTitleCase(header: string): string {
    return header
      // Insert space before uppercase letters that follow lowercase (camelCase)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Replace underscores/hyphens with spaces
      .replace(/[_\-]+/g, ' ')
      // Capitalise first letter of each word, lowercase the rest
      .split(' ')
      .filter(w => w.length > 0)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
}
